import { cannot } from '@/app/helpers'
import { formatMessageLabel, getMessagePlaceholderLabel } from '@/app/module/campaigns/label-helpers'
import {
  APIMessage,
  ContentFlow,
  ContentFlowElement,
  Message,
  PartFamily,
  SMSQuestion,
} from '@/app/module/campaigns/types'
import { PersonalizationType, SinglePersonalizationType } from '@/app/types'

export type WaFormAnswerPzSource = {
  flow: ContentFlow
  label: string
}

enum WaFormAnswerField {
  AnswerText = 'answerText',
  Choice = 'choice',
  Choices = 'choices',
  ChoiceJson = 'choiceJson',
  ChoicesJson = 'choicesJson',
}

enum SMSAnswerField {
  AnswerText = 'answerText',
  Choice = 'choice',
  ChoiceValid = 'choiceValid',
  Category = 'category',
}

const getAnswerPzValue = (id: string, field: SMSAnswerField | WaFormAnswerField): string =>
  `sub.answer.id.${id}.${field}`

const apiResponseFields = {
  body: 'Response Body',
  bodySnippet: 'Response Snippet',
  statusCode: 'Status Code',
  success: 'Success',
  contentType: 'Content Type',
}

const getAPIResponsePz = (
  message: APIMessage,
  index: number,
  prefix = `sub.apicall.id.${message.id}`,
): SinglePersonalizationType[] => {
  const label = formatMessageLabel(
    index,
    message.label || getMessagePlaceholderLabel(message.type, message.hasQuestion).label,
  )

  return Object.entries(apiResponseFields).map(([field, fieldLabel]) => ({
    access: true,
    label: `${label}: ${fieldLabel}`,
    value: `${prefix}.${field}`,
  }))
}

const getSMSAnswerFields = (message: SMSQuestion): SMSAnswerField[] => {
  if (!message.openEnded) {
    return [SMSAnswerField.Choice, SMSAnswerField.ChoiceValid]
  }

  const fields = [SMSAnswerField.AnswerText, SMSAnswerField.ChoiceValid]
  if (message.useAi && message.openEnded.categories?.length) {
    fields.push(SMSAnswerField.Category)
  }
  return fields
}

const getSMSAnswerFieldLabel = (field: SMSAnswerField): string => {
  switch (field) {
    case SMSAnswerField.AnswerText:
      return 'Answer text'
    case SMSAnswerField.Choice:
      return 'Choice'
    case SMSAnswerField.ChoiceValid:
      return 'Answered'
    case SMSAnswerField.Category:
      return 'AI category'
    default:
      return cannot(field)
  }
}

const getSMSAnswerPz = (message: SMSQuestion, index: number): SinglePersonalizationType[] => {
  const label = formatMessageLabel(
    index,
    message.label || getMessagePlaceholderLabel(message.type, message.hasQuestion).label,
  )

  return getSMSAnswerFields(message).map((field) => ({
    access: true,
    label: `${label}: ${getSMSAnswerFieldLabel(field)}`,
    value: getAnswerPzValue(message.id, field),
  }))
}

type WaFormPrimaryAnswerField = WaFormAnswerField.AnswerText | WaFormAnswerField.Choice | WaFormAnswerField.Choices

const getWaFormAnswerFields = (answerField: WaFormPrimaryAnswerField): WaFormAnswerField[] => {
  switch (answerField) {
    case WaFormAnswerField.AnswerText:
      return [WaFormAnswerField.AnswerText, WaFormAnswerField.ChoiceJson]
    case WaFormAnswerField.Choice:
      return [WaFormAnswerField.Choice, WaFormAnswerField.ChoiceJson]
    case WaFormAnswerField.Choices:
      return [WaFormAnswerField.Choices, WaFormAnswerField.ChoicesJson]
    default:
      return cannot(answerField)
  }
}

const getWaFormAnswerFieldLabel = (field: WaFormAnswerField, answerField: WaFormPrimaryAnswerField): string => {
  switch (field) {
    case WaFormAnswerField.AnswerText:
      return 'Answer text'
    case WaFormAnswerField.Choice:
      return 'Choice'
    case WaFormAnswerField.Choices:
      return 'Choices'
    case WaFormAnswerField.ChoiceJson:
      return answerField === WaFormAnswerField.AnswerText ? 'Answer text (JSON string)' : 'Choice (JSON string)'
    case WaFormAnswerField.ChoicesJson:
      return 'Choices (JSON array)'
    default:
      return cannot(field)
  }
}

const getQuestionText = (element: ContentFlowElement) =>
  element.shortText?.question ||
  element.longText?.question ||
  element.datePicker?.question ||
  element.singleSelect?.question ||
  element.multiSelect?.question ||
  element.questionId

const getAnswerField = (element: ContentFlowElement): WaFormPrimaryAnswerField | null => {
  switch (true) {
    case !!element.multiSelect:
      return WaFormAnswerField.Choices
    case !!element.singleSelect:
      return WaFormAnswerField.Choice
    case !!element.shortText:
    case !!element.longText:
    case !!element.datePicker:
      return WaFormAnswerField.AnswerText
    default:
      return null
  }
}

const getAnswerLabel = (element: ContentFlowElement, sourceLabel: string) => {
  const question = getQuestionText(element)

  return `${sourceLabel}: ${question}`
}

export const getWaFormAnswerPz = ({ flow, label }: WaFormAnswerPzSource): SinglePersonalizationType[] =>
  flow.pages.flatMap((page) =>
    page.elements.flatMap((element) => {
      const answerField = getAnswerField(element)
      if (!element.questionId || !answerField) {
        return []
      }

      return getWaFormAnswerFields(answerField).map((field) => ({
        access: true,
        label: `${getAnswerLabel(element, label)}: ${getWaFormAnswerFieldLabel(field, answerField)}`,
        value: getAnswerPzValue(element.questionId, field),
      }))
    }),
  )

export const withPartResponsePz = (
  pzList: PersonalizationType,
  responses: SinglePersonalizationType[],
  prepend = false,
): PersonalizationType => {
  if (!responses.length) {
    return pzList
  }

  const { contact, customfields, partResponses: existingResponses = [], ...rest } = pzList

  return {
    ...(contact ? { contact } : {}),
    partResponses: prepend ? [...responses, ...existingResponses] : [...existingResponses, ...responses],
    ...(customfields ? { customfields } : {}),
    ...rest,
  }
}

export const withCurrentWaFormPz = (
  pzList: PersonalizationType,
  flow: ContentFlow,
  label: string,
): PersonalizationType => withPartResponsePz(pzList, getWaFormAnswerPz({ flow, label }), true)

export const withCurrentAPIResponsePz = (
  pzList: PersonalizationType,
  message: APIMessage,
  index: number,
): PersonalizationType => withPartResponsePz(pzList, getAPIResponsePz(message, index, 'api'), true)

export const withWaFormAnswerPz = (pzList: PersonalizationType, sources: WaFormAnswerPzSource[]): PersonalizationType =>
  withPartResponsePz(
    pzList,
    sources.flatMap((source) => getWaFormAnswerPz(source)),
  )

export const getPartResponsePz = (message: Message, index: number): SinglePersonalizationType[] => {
  switch (message.type) {
    case PartFamily.WhatsAppFlow:
      return getWaFormAnswerPz({
        flow: message.flow,
        label: formatMessageLabel(index, message.label || 'WhatsApp Form'),
      })
    case PartFamily.SMS:
      return message.hasQuestion ? getSMSAnswerPz(message, index) : []
    case PartFamily.API:
      return getAPIResponsePz(message, index)
    case PartFamily.Voice:
    case PartFamily.WhatsApp:
    case PartFamily.Action:
    case PartFamily.CATI:
    case PartFamily.QuotaActions:
    case PartFamily.Topup:
      return []
    default:
      return cannot(message)
  }
}

type GetMessagePersonalizationListParams = {
  id: string
  messages: Partial<Record<string, Message>>
  ordered: string[]
  personalizationList: PersonalizationType
}

export const getMessagePersonalizationList = ({
  id,
  messages,
  ordered,
  personalizationList,
}: GetMessagePersonalizationListParams): PersonalizationType => {
  const currentIndex = ordered.indexOf(id)
  if (currentIndex < 1) {
    return personalizationList
  }

  const responses = ordered
    .slice(0, currentIndex)
    .map((messageId, index) => ({ index, message: messages[messageId] }))
    .reverse()
    .flatMap(({ index, message }) => (message ? getPartResponsePz(message, index) : []))

  return withPartResponsePz(personalizationList, responses)
}
